
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Layout } from './Layout';
import { Button, Card, StatusBadge, Input, Select, Checkbox, Badge } from './ui';
import { OrderModal } from './OrderModal';
import { KanbanBoard } from './KanbanBoard';
import { LandingPage } from './LandingPage';
import { Auth } from './Auth';
import { Settings } from './Settings';
import { Onboarding } from './Onboarding';
import { Financials } from './Financials';
import { api } from '../services/api';
import { supabase } from '../services/supabase';
import { User, UserRole, Order, OrderStatus, FileUploadProgress, MillingCenter, AppNotification, DesignerServiceRate, Transaction, TopUpRequest, OrderFile } from '../types';
import { translations, Language } from '../services/translations';
import { AdminPanel } from './AdminPanel';
import { STLViewer } from './STLViewer';
import { UploadWidget } from './UploadWidget';
import { ChatWidget } from './ChatWidget';
import { OrderTimeline } from './OrderTimeline';
import { PublicOrderView } from './PublicOrderView';
import { PublicUploadView } from './PublicUploadView';
import { ContactRequestView } from './ContactRequestView';
import { ClinicManagement } from './ClinicManagement';
import { PriceList } from './PriceList';
import { DentalArchSelector } from './DentalArchSelector';
import { DentistDashboard } from './DentistDashboard';
import { DentistOrders } from './DentistOrders';
import {
   Users, Calendar, Activity, DollarSign, Settings as SettingsIcon, LogOut,
   Search, Bell, Menu, X, Filter, Download, ChevronRight,
   ChevronLeft, Plus, FileText, CheckCircle, Clock,
   AlertTriangle, Upload, Trash2, Edit2, Share2,
   MoreVertical, Copy as CopyIcon, MessageSquare,
   Send, Paperclip, Minimize2, Maximize2, Flag,
   ArrowRight, FolderOpen, Package, Lock, Calculator,
   Palette, FileCheck, Loader2, Cpu, User as UserIcon, Phone, Check,
   Grip
} from 'lucide-react';
import JSZip from 'jszip';

const NotificationDropdown = () => {
   return (
      <div className="absolute top-16 right-4 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden z-[500] border border-gray-200 dark:border-gray-700">
         <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
               <Bell className="w-4 h-4" />
               Notifications
            </h3>
         </div>
         <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
            <p>No new notifications</p>
         </div>
      </div>
   );
};

export default function App() {
   const [hash, setHash] = useState(window.location.hash || '#/landing');
   const [user, setUser] = useState<User | null>(null);
   const [orders, setOrders] = useState<Order[]>([]);
   const [loading, setLoading] = useState(false);
   const [isModalOpen, setIsModalOpen] = useState(false);



   const [lang, setLang] = useState<Language>('en');
   const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
   const [users, setUsers] = useState<User[]>([]);
   const [designerRates, setDesignerRates] = useState<DesignerServiceRate[]>([]);

   const [activeDetailTab, setActiveDetailTab] = useState<'overview' | 'communication' | 'files' | 'admin'>('overview');
   const [notifications, setNotifications] = useState<AppNotification[]>([]);
   const [showNotifications, setShowNotifications] = useState(false);
   const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
   const [millingCenters, setMillingCenters] = useState<MillingCenter[]>([]);
   const [showMillingModal, setShowMillingModal] = useState(false);
   const [generatedLinkData, setGeneratedLinkData] = useState<{ link: string, whatsapp: string, centerName: string } | null>(null);

   const [showSubmitModal, setShowSubmitModal] = useState(false);
   const [submissionFiles, setSubmissionFiles] = useState<{ html: File | null, zip: File | null }>({ html: null, zip: null });
   const [submitting, setSubmitting] = useState(false);
   const [uploads, setUploads] = useState<FileUploadProgress[]>([]);


   const [editingOrder, setEditingOrder] = useState<Order | null>(null);
   const [showFeedbackModal, setShowFeedbackModal] = useState(false);
   const [feedbackType, setFeedbackType] = useState<'RE_DESIGN' | 'RE_TRY' | null>(null);
   const [feedbackNote, setFeedbackNote] = useState('');
   const [feedbackFiles, setFeedbackFiles] = useState<File[]>([]);
   const [openMenuId, setOpenMenuId] = useState<string | null>(null);

   // Financial State for Dentists
   const [transactions, setTransactions] = useState<Transaction[]>([]);
   const [myTopUpRequests, setMyTopUpRequests] = useState<TopUpRequest[]>([]);

   // Archive Inspector State
   const [archiveFile, setArchiveFile] = useState<File | Blob | null>(null);
   const [archiveContent, setArchiveContent] = useState<string[]>([]);
   const [viewingSTL, setViewingSTL] = useState<string | null>(null);
   const [showArchiveModal, setShowArchiveModal] = useState(false);

   // Sidebar State
   const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

   const t = translations[lang];

   useEffect(() => {
      const handleHashChange = () => setHash(window.location.hash || '#/landing');
      window.addEventListener('hashchange', handleHashChange);
      if (!window.location.hash) window.location.hash = '#/landing';
      return () => window.removeEventListener('hashchange', handleHashChange);
   }, []);

   useEffect(() => {
      const handleClick = () => setOpenMenuId(null);
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
   }, []);

   const navigate = (path: string) => { window.location.hash = path; };

   const getActiveTab = () => {
      const cleanPath = hash.replace('#/', '').split('?')[0];
      const parts = cleanPath.split('/');
      if (parts[0] === 'dashboard') return parts[1] || 'dashboard';
      return 'dashboard';
   };
   const activeTab = getActiveTab();
   const handleTabChange = (tabId: string) => navigate(tabId === 'dashboard' ? '#/dashboard' : `#/dashboard/${tabId}`);

   useEffect(() => {
      // Initial User Load
      api.getCurrentUser().then(async u => {
         if (u) {
            setUser(u);
            if (u.role === UserRole.DENTIST && !u.pricePlanId) {
               await api.ensureDefaultPricePlan(u.id);
            }
            const currentPath = hash.replace('#/', '').split('?')[0];
            if (!hash.includes('order/') && !hash.includes('request-upload/') && !hash.includes('contact-request/')) {
               if (['landing', 'login', 'signup', ''].includes(currentPath)) navigate('#/dashboard');
            }
            loadNotifications();
         }
      });

      // Auth State Listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
         if (event === 'SIGNED_IN') {
            api.getCurrentUser().then(u => {
               if (u) {
                  setUser(prev => {
                     if (prev && JSON.stringify(prev) === JSON.stringify(u)) return prev;
                     return u;
                  });
                  const h = window.location.hash;
                  if (h === '#/landing' || h === '#/login' || h === '#/signup' || h === '' || h === '#/') {
                     navigate('#/dashboard');
                  }
                  loadNotifications();
               }
            });
         } else if (event === 'SIGNED_OUT') {
            setUser(null);
            if (!window.location.hash.includes('order/') && !window.location.hash.includes('request-upload/') && !window.location.hash.includes('contact-request/')) {
               navigate('#/landing');
            }
         }
      });
      return () => subscription.unsubscribe();
   }, []);

   useEffect(() => {
      if (user) {
         loadOrders();
         if (user.role === UserRole.ADMIN) {
            api.getUsers().then(setUsers);
            api.getMillingCenters().then(setMillingCenters);
            api.getAllDesignerRates().then(setDesignerRates);
         }
         if (user.role === UserRole.DENTIST || (user.role === UserRole.MODERATOR && user.canSeeFinancials)) {
            api.getTransactions(user.id).then(setTransactions);
            api.getTopUpRequests().then(setMyTopUpRequests);
         }

         const changesChannel = supabase.channel('public:changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => loadOrders(false))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'order_files' }, () => loadOrders(false))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items' }, () => loadOrders(false))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'timeline_events' }, () => loadOrders(false))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions' }, () => {
               if (user) api.getTransactions(user.id).then(setTransactions);
            })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'top_up_requests' }, () => {
               if (user && (user.role === UserRole.DENTIST || user.role === UserRole.MODERATOR)) {
                  api.getTopUpRequests().then(setMyTopUpRequests);
               }
            })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` }, () => {
               api.getCurrentUser().then(u => {
                  if (u) {
                     setUser(prev => {
                        if (prev && JSON.stringify(prev) === JSON.stringify(u)) return prev;
                        return u;
                     });
                  }
               });
            })
            .subscribe();
         const notifChannel = supabase.channel(`notifications:${user.id}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, () => loadNotifications())
            .subscribe();
         return () => { supabase.removeChannel(changesChannel); supabase.removeChannel(notifChannel); };
      }
   }, [user]);

   const loadOrders = async (showLoading = true) => {
      if (showLoading && orders.length === 0) setLoading(true);
      const data = await api.getOrders();
      setOrders(data);
      setLoading(false);
      if (selectedOrder) {
         const updated = data.find(o => o.id === selectedOrder.id);
         if (updated) setSelectedOrder(updated);
         else setSelectedOrder(null);
      }
   };

   const loadNotifications = async () => { setNotifications(await api.getNotifications()); };

   const handleQueueUpload = (files: File[], targetOrderId?: string) => {
      const newUploads = files.map(f => ({
         id: Math.random().toString(36).substr(2, 9),
         fileName: f.name,
         progress: 0,
         status: 'uploading' as const,
         fileObj: f
      }));
      setUploads(prev => [...prev, ...newUploads]);

      if (targetOrderId) {
         newUploads.forEach(async (u) => {
            const interval = setInterval(() => {
               setUploads(curr => curr.map(c => {
                  if (c.id === u.id && c.status === 'uploading') {
                     const next = c.progress + 10;
                     return { ...c, progress: next > 90 ? 90 : next };
                  }
                  return c;
               }));
            }, 500);

            try {
               if (u.fileObj) {
                  await api.uploadFile(targetOrderId, u.fileObj);
               }
               clearInterval(interval);
               setUploads(curr => curr.map(c => c.id === u.id ? { ...c, progress: 100, status: 'completed' } : c));
            } catch (e) {
               clearInterval(interval);
               setUploads(curr => curr.map(c => c.id === u.id ? { ...c, status: 'error' } : c));
            }
         });
      } else {
         newUploads.forEach(u => setTimeout(() => setUploads(curr => curr.map(c => c.id === u.id ? { ...c, progress: 100, status: 'completed' } : c)), 2000));
      }
   };

   const handleRemoveUpload = (id: string) => setUploads(prev => prev.filter(u => u.id !== id));

   const handleSaveOrder = async (orderData: any, files: File[]) => {
      try {
         if (editingOrder) {
            await api.updateOrderDetails(editingOrder.id, orderData);
            if (files.length > 0) {
               handleQueueUpload(files, editingOrder.id);
               api.notifyFileUpload(editingOrder.id, user?.name || 'User', files.length);
            }
            setEditingOrder(null);
            setIsModalOpen(false);
            loadOrders(false);
         } else {
            const payload = {
               ...orderData,
               doctorId: user?.role === UserRole.MODERATOR ? user.parentId : (user?.role === UserRole.ADMIN ? orderData.doctorId : user?.id),
               creatorId: user?.id
            };
            const newOrder = await api.createOrder(payload, []);
            if (newOrder && files.length > 0) {
               handleQueueUpload(files, newOrder.id);
            }

            setIsModalOpen(false);
            await loadOrders(false);

            if (user?.role === UserRole.ADMIN) {
               navigate('#/dashboard/all_orders');
            } else {
               navigate('#/dashboard/orders');
            }

            if (user?.role === UserRole.DENTIST && !user.isTrusted) {
               if (confirm("Order Created Successfully! \n\nGo to payments now?")) {
                  navigate('#/dashboard/invoices');
               }
            }
            return newOrder;
         }
      } catch (e: any) {
         console.error("Order Save Error:", e);
         throw e;
      }
   };

   const handleUploadToOrder = (files: File[]) => {
      if (!selectedOrder) return;
      handleQueueUpload(files, selectedOrder.id);
      api.notifyFileUpload(selectedOrder.id, user?.name || 'User', files.length);
   };

   const handleStatusChange = async (orderId: string, status: OrderStatus) => {
      if (status === OrderStatus.OUT_FOR_DELIVERY) {
         if (!confirm("Is this case ready for pickup/delivery?")) return;
      }

      if (status === OrderStatus.DELIVERED) {
         const order = orders.find(o => o.id === orderId);
         // Only intercept for PMMA if it's currently in a production/logistics stage BEFORE the first delivery
         if (order?.isPMMA &&
            order.status !== OrderStatus.IN_TRY_IN &&
            order.status !== OrderStatus.MILLING &&
            order.status !== OrderStatus.READY) {

            // Check if this case has already been approved for final restoration
            // We fetch the timeline to be absolutely sure
            try {
               const events = await api.getTimeline(orderId);
               const isFinalRestoration = events.some(e =>
                  e.status === OrderStatus.MILLING ||
                  e.title.includes('Try-In Approved')
               );

               if (!isFinalRestoration) {
                  await api.logTimelineEvent(orderId, 'Try-In Dispatched', 'PMMA try-in sent to clinic.', OrderStatus.IN_TRY_IN);
                  await api.updateOrderStatus(orderId, OrderStatus.IN_TRY_IN, "PMMA Delivered - Awaiting Try-In Result");
                  loadOrders(false);
                  return;
               }
            } catch (e) {
               console.error("Timeline check failed, falling back to interception", e);
               // If timeline fails, we err on the side of caution for PMMA
               await api.logTimelineEvent(orderId, 'Try-In Dispatched', 'PMMA try-in sent to clinic.', OrderStatus.IN_TRY_IN);
               await api.updateOrderStatus(orderId, OrderStatus.IN_TRY_IN, "PMMA Delivered - Awaiting Try-In Result");
               loadOrders(false);
               return;
            }
         }
      }
      await api.updateOrderStatus(orderId, status);
   };

   const handleLogout = async () => { await supabase.auth.signOut(); navigate('#/landing'); };
   const handleAssignTechnician = async (techId: string) => { if (selectedOrder) await api.assignTechnician(selectedOrder.id, techId); };
   const handleDuplicateOrder = async (order: Order) => { if (confirm("Duplicate this order?")) { await api.duplicateOrder(order, user!.id); alert("Order duplicated."); } };

   const handleDeleteOrder = async (orderId: string) => {
      if (confirm(`⚠️ Are you sure you want to delete order #${orderId.slice(0, 6)}? \n\nThis cannot be undone.`)) {
         try {
            await api.deleteOrder(orderId);
            if (selectedOrder?.id === orderId) setSelectedOrder(null);
            setOrders(prev => prev.filter(o => o.id !== orderId));
         } catch (e) {
            console.error(e);
            alert("Failed to delete order.");
         }
      }
   };

   const handleEditOrder = (order: Order) => {
      setEditingOrder(order);
      setIsModalOpen(true);
   };

   const handleGenerateMillingLink = async (center: MillingCenter) => {
      if (!selectedOrder) return;
      try {
         const token = await api.createShareLink(selectedOrder.id);
         const link = `${window.location.origin}/#/order/${token}`;
         const cleanNumber = center.whatsappNumber.replace(/\D/g, '');
         const msg = `New Order from Galal Lab:\nPatient: ${selectedOrder.patientName}\nView Order: ${link}`;
         const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(msg)}`;
         setGeneratedLinkData({ link, whatsapp: whatsappUrl, centerName: center.name });
         await api.setOrderMillingCenter(selectedOrder.id, center.id);
         await api.updateOrderStatus(selectedOrder.id, OrderStatus.MILLING, `Sent to ${center.name}`);
      } catch (e) { console.error(e); alert("Failed link generation"); }
   };
   const handleCopyLink = () => { if (generatedLinkData?.link) { navigator.clipboard.writeText(generatedLinkData.link); alert("Copied!"); } };
   const handleDesignerSubmit = async () => {
      if (!selectedOrder || !submissionFiles.html || !submissionFiles.zip) return alert("Required files missing");
      setSubmitting(true);
      try {
         const htmlUrl = await api.uploadFile(selectedOrder.id, submissionFiles.html);
         const zipUrl = await api.uploadFile(selectedOrder.id, submissionFiles.zip);
         await api.submitDesign(selectedOrder.id, htmlUrl, zipUrl, selectedOrder.requiresDesignCheck);
         setShowSubmitModal(false); setSubmissionFiles({ html: null, zip: null });
      } catch (err) { console.error(err); alert("Failed"); } finally { setSubmitting(false); }
   };
   const handleDoctorAction = async (action: 'approve' | 'reject') => {
      if (!selectedOrder) return;
      if (action === 'approve') {
         try {
            await api.moveToMilling(selectedOrder);
            // Refresh orders to show updated status
            await loadOrders(false);
            // Refresh user data to update balance
            const updatedUser = await api.getCurrentUser();
            if (updatedUser) setUser(updatedUser);
            // Close the modal
            setSelectedOrder(null);
            alert("Payment Successful! Case moved to production.");
         } catch (e: any) {
            alert(e.message || "Payment Failed."); return;
         }
      } else {
         await api.updateOrderStatus(selectedOrder.id, OrderStatus.DESIGNING, 'Rejected by doctor');
      }
   };
   const handleTopUpRequest = async (amount: number, file: File, orderId?: string) => {
      try {
         await api.requestTopUp(amount, file, orderId);
         if (user) {
            api.getTransactions(user.id).then(setTransactions);
            api.getTopUpRequests().then(setMyTopUpRequests);
            api.getCurrentUser().then(u => { if (u) setUser(u); });
            if (orderId) loadOrders(false);
         }
      } catch (e: any) {
         throw new Error(e.message || "Payment Failed");
      }
   };
   const getDaysRemaining = (dueDateStr: string) => { if (!dueDateStr) return null; return Math.ceil((new Date(dueDateStr).getTime() - new Date().getTime()) / (86400000)); };
   const getUrgencyColor = (order: Order) => { if (order.urgency === 'Urgent') return 'border-l-4 border-l-red-500'; const d = getDaysRemaining(order.dueDate); return (d !== null && d <= 2) ? 'border-l-4 border-l-orange-500' : 'border-l-4 border-l-transparent'; };

   const handleTryInProceed = async () => {
      if (!selectedOrder) return;
      if (confirm("Confirm Try-In Success? Case will proceed to FINAL MILLING.")) {
         await api.logTimelineEvent(selectedOrder.id, 'Try-In Approved', 'Doctor approved PMMA try-in. Proceeding to final restoration.', OrderStatus.MILLING);
         await api.updateOrderStatus(selectedOrder.id, OrderStatus.MILLING, "Try-In Approved");
      }
   };

   const handleOpenFeedback = (type: 'RE_DESIGN' | 'RE_TRY') => {
      setFeedbackType(type);
      setFeedbackNote('');
      setFeedbackFiles([]);
      setShowFeedbackModal(true);
   };

   const handleSubmitFeedback = async () => {
      if (!selectedOrder || !feedbackType) return;
      if (!feedbackNote) return alert("Please provide notes explaining the issue.");

      setSubmitting(true);
      try {
         for (const file of feedbackFiles) {
            await api.uploadFile(selectedOrder.id, file);
         }

         const newStatus = feedbackType === 'RE_DESIGN' ? OrderStatus.DESIGNING : OrderStatus.MILLING;
         const title = feedbackType === 'RE_DESIGN' ? 'Try-In Rejected: Design Change' : 'Try-In Rejected: Reprint Request';

         await api.logTimelineEvent(selectedOrder.id, title, feedbackNote, newStatus);
         await api.sendMessage(selectedOrder.id, user!.id, `FEEDBACK: ${feedbackNote}`);
         await api.updateOrderStatus(selectedOrder.id, newStatus, feedbackNote);

         setShowFeedbackModal(false);
      } catch (e) {
         console.error(e);
         alert("Failed to submit feedback");
      } finally {
         setSubmitting(false);
      }
   };

   // Archive Handling Logic
   const handleInspectArchive = async (file: OrderFile) => {
      let blob: Blob;
      if (file.url) {
         try {
            const res = await fetch(file.url);
            blob = await res.blob();
         } catch (e) {
            console.error("Fetch failed", e);
            return alert("Cannot fetch file content. CORS issue or file missing.");
         }
      } else {
         return alert("Invalid file reference.");
      }
      setArchiveFile(blob);
      setViewingSTL(null);

      try {
         const zip = await JSZip.loadAsync(blob);
         const files: string[] = [];
         zip.forEach((relativePath) => files.push(relativePath));
         setArchiveContent(files);
         setShowArchiveModal(true);
      } catch (e) {
         alert("Could not open archive. Ensure it is a valid ZIP file. RAR files are not supported in browser preview.");
      }
   };

   const handleViewArchivedSTL = async (filename: string) => {
      if (!archiveFile) return;
      try {
         const zip = await JSZip.loadAsync(archiveFile);
         const file = zip.file(filename);
         if (file) {
            const blob = await file.async('blob');
            const url = URL.createObjectURL(blob);
            setViewingSTL(url);
         }
      } catch (e) {
         console.error(e);
      }
   };





   const renderTechnicianContent = () => {
      // If Admin, show all. If Tech, show assigned.
      const myTasks = orders.filter(o => o.technicianId === user?.id || o.status === OrderStatus.PENDING); // PENDING visible to pick up? Or just assigned? Assuming Tech sees assigned.
      // Actually, usually Tech sees what's assigned. If pool is PENDING, maybe they can self-assign?
      // Let's stick to assigned + active for workbench.
      const assignedTasks = orders.filter(o => o.technicianId === user?.id && o.status !== OrderStatus.DELIVERED);

      return (
         <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
               <h2 className="text-2xl font-bold text-white">Workbench</h2>
               <div className="bg-zinc-900 px-4 py-2 rounded-lg border border-zinc-800 text-zinc-400">
                  Assigned: <span className="text-white font-bold">{assignedTasks.length}</span>
               </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {assignedTasks.map(order => (
                  <div key={order.id} onClick={() => setSelectedOrder(order)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-blue-500 transition-all cursor-pointer group relative overflow-hidden">
                     <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                     <div className="flex justify-between items-start mb-4">
                        <div>
                           <h3 className="font-bold text-white text-lg">{order.patientName}</h3>
                           <p className="text-xs text-zinc-500 font-mono">#{order.id.slice(0, 8)}</p>
                        </div>
                        <StatusBadge status={order.status} />
                     </div>

                     <div className="space-y-2 mb-4">
                        {order.items.map((item, i) => (
                           <div key={i} className="text-sm text-zinc-300 bg-black/30 p-2 rounded border border-white/5 flex justify-between">
                              <span>{item.serviceName}</span>
                              <span className="font-mono text-zinc-500">{item.teeth.join(',')}</span>
                           </div>
                        ))}
                     </div>

                     <div className="flex justify-between items-center pt-4 border-t border-zinc-800">
                        <div className="flex items-center gap-2 text-xs text-zinc-400">
                           <Clock size={14} /> Due: {new Date(order.dueDate).toLocaleDateString()}
                        </div>
                        <Button size="sm" variant="secondary">Open</Button>
                     </div>
                  </div>
               ))}
               {assignedTasks.length === 0 && (
                  <div className="col-span-full text-center py-20 text-zinc-500 bg-zinc-900/50 rounded-xl border border-zinc-800 border-dashed">
                     No active tasks assigned.
                  </div>
               )}
            </div>
         </div>
      );
   };



   const renderSubmitModal = () => showSubmitModal && (
      <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 animate-fade-in">
         <Card className="w-full max-w-md bg-surface border-border">
            <h2 className="text-xl font-bold text-primary mb-6">Submit Design Files</h2>
            <div className="space-y-4">
               <div>
                  <label className="block text-xs font-bold text-secondary uppercase mb-2">HTML Preview</label>
                  <input type="file" accept=".html" onChange={(e) => setSubmissionFiles({ ...submissionFiles, html: e.target.files?.[0] || null })} className="block w-full text-sm text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-surfaceHighlight file:text-primary hover:file:bg-border" />
               </div>
               <div>
                  <label className="block text-xs font-bold text-secondary uppercase mb-2">Production ZIP</label>
                  <input type="file" accept=".zip" onChange={(e) => setSubmissionFiles({ ...submissionFiles, zip: e.target.files?.[0] || null })} className="block w-full text-sm text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-surfaceHighlight file:text-primary hover:file:bg-border" />
               </div>
               <div className="flex gap-3 pt-4">
                  <Button variant="secondary" onClick={() => setShowSubmitModal(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleDesignerSubmit} disabled={submitting} className="flex-1">{submitting ? 'Uploading...' : 'Submit'}</Button>
               </div>
            </div>
         </Card>
      </div>
   );

   const renderMillingModal = () => showMillingModal && selectedOrder && (
      <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 animate-fade-in">
         <Card className="w-full max-w-lg bg-surface border-border">
            <div className="flex justify-between items-center mb-6">
               <h2 className="text-xl font-bold text-primary">Send to Milling Center</h2>
               <button onClick={() => setShowMillingModal(false)}><X size={20} className="text-secondary hover:text-primary" /></button>
            </div>

            {!generatedLinkData ? (
               <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {millingCenters.map(center => (
                     <div key={center.id} onClick={() => handleGenerateMillingLink(center)} className="flex justify-between items-center p-4 bg-surfaceHighlight border border-border rounded-xl hover:bg-border cursor-pointer transition-colors group">
                        <div>
                           <div className="font-bold text-primary group-hover:text-blue-500">{center.name}</div>
                           <div className="text-xs text-secondary flex items-center gap-1"><Phone size={10} /> {center.whatsappNumber}</div>
                        </div>
                        <ChevronRight size={16} className="text-secondary group-hover:text-primary" />
                     </div>
                  ))}
               </div>
            ) : (
               <div className="space-y-6 text-center animate-slide-up">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto text-green-500">
                     <CheckCircle size={32} />
                  </div>
                  <div>
                     <h3 className="text-lg font-bold text-primary">Link Generated for {generatedLinkData.centerName}</h3>
                     <p className="text-sm text-secondary mt-2">Send this link via WhatsApp to the milling center.</p>
                  </div>

                  <div className="flex items-center gap-2 bg-surfaceHighlight p-3 rounded-lg border border-border">
                     <input readOnly value={generatedLinkData.link} className="bg-transparent text-sm text-primary w-full outline-none" />
                     <button onClick={handleCopyLink} className="text-secondary hover:text-primary"><CopyIcon size={16} /></button>
                  </div>

                  <div className="flex gap-3">
                     <Button variant="secondary" onClick={() => setGeneratedLinkData(null)} className="flex-1">Back</Button>
                     <a href={generatedLinkData.whatsapp} target="_blank" rel="noreferrer" className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl font-bold flex items-center justify-center gap-2 py-3 transition-colors">
                        <MessageSquare size={18} /> Send WhatsApp
                     </a>
                  </div>
               </div>
            )}
         </Card>
      </div>
   );

   const renderFeedbackModal = () => showFeedbackModal && (
      <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 animate-fade-in">
         <Card className="w-full max-w-lg bg-surface border-border">
            <h2 className="text-xl font-bold text-primary mb-4">
               {feedbackType === 'RE_DESIGN' ? 'Request Re-Design' : 'Request Reprint (Try-In Rejected)'}
            </h2>
            <div className="space-y-4">
               <textarea
                  className="w-full h-32 bg-surfaceHighlight border border-border rounded-xl p-4 text-primary outline-none focus:border-red-500"
                  placeholder="Describe the issue and what needs to be changed..."
                  value={feedbackNote}
                  onChange={(e) => setFeedbackNote(e.target.value)}
               />
               <div>
                  <label className="block text-xs font-bold text-secondary uppercase mb-2">Attach Photos (Optional)</label>
                  <input type="file" multiple onChange={(e) => setFeedbackFiles(Array.from(e.target.files || []))} className="text-sm text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-surfaceHighlight file:text-primary hover:file:bg-border" />
               </div>
               <div className="flex gap-3 pt-4">
                  <Button variant="secondary" onClick={() => setShowFeedbackModal(false)} className="flex-1">Cancel</Button>
                  <Button onClick={handleSubmitFeedback} disabled={submitting} variant="danger" className="flex-1">{submitting ? 'Submitting...' : 'Send Feedback'}</Button>
               </div>
            </div>
         </Card>
      </div>
   );

   const renderOrderDetailModal = () => {
      if (!selectedOrder) return null;

      return (
         <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center animate-fade-in">
            <div className="w-full h-full md:max-w-6xl md:h-[90vh] bg-background md:border border-border md:rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">

               {/* Archive Inspector Modal Overlay */}
               {showArchiveModal && (
                  <div className="absolute inset-0 z-[60] bg-background/95 flex flex-col p-6 animate-fade-in">
                     <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-primary flex items-center gap-2"><FolderOpen size={20} /> Archive Contents</h3>
                        <button onClick={() => setShowArchiveModal(false)} className="text-secondary hover:text-primary"><X size={24} /></button>
                     </div>
                     <div className="flex-1 flex gap-4 overflow-hidden">
                        <div className="w-1/3 bg-surface border border-border rounded-xl overflow-y-auto p-2 custom-scrollbar">
                           {archiveContent.map(f => (
                              <div key={f} className="p-2 hover:bg-surfaceHighlight rounded cursor-pointer flex justify-between items-center group">
                                 <span className="text-sm text-secondary truncate w-3/4" title={f}>{f}</span>
                                 {f.toLowerCase().endsWith('.stl') && (
                                    <button onClick={() => handleViewArchivedSTL(f)} className="p-1 bg-blue-600 rounded text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">View</button>
                                 )}
                              </div>
                           ))}
                        </div>
                        <div className="flex-1 bg-black rounded-xl border border-zinc-800 relative overflow-hidden">
                           {viewingSTL ? <STLViewer url={viewingSTL} /> : <div className="absolute inset-0 flex items-center justify-center text-zinc-500">Select an STL file from the list to preview</div>}
                        </div>
                     </div>
                  </div>
               )}

               {/* Header */}
               <div className="flex flex-col md:flex-row justify-between md:items-center p-4 md:p-6 border-b border-border bg-surface gap-3 md:gap-4">
                  <div className="flex items-start gap-2 md:gap-4">
                     <button onClick={() => setSelectedOrder(null)} className="md:hidden text-secondary mt-0.5 p-1 -ml-1 hover:bg-surfaceHighlight rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"><ArrowRight className="rotate-180" size={24} /></button>
                     <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary shrink-0 border border-primary/20">
                        <Package size={20} className="md:w-6 md:h-6" />
                     </div>
                     <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                           <h2 className="text-lg md:text-2xl font-bold text-primary truncate">{selectedOrder.patientName}</h2>
                           <StatusBadge status={selectedOrder.status} />
                        </div>
                        <div className="flex items-center gap-2 md:gap-3 mt-1 text-xs md:text-sm text-secondary flex-wrap">
                           <span className="font-mono bg-surfaceHighlight px-1.5 md:px-2 py-0.5 rounded border border-border text-[10px] md:text-xs">#{selectedOrder.id.slice(0, 8)}</span>
                           <span className="flex items-center gap-1 whitespace-nowrap"><span className="w-1 h-1 rounded-full bg-zinc-500"></span> Tooth {selectedOrder.toothNumber}</span>
                           <span className="flex items-center gap-1 truncate"><span className="w-1 h-1 rounded-full bg-zinc-500 shrink-0"></span> <span className="truncate">{selectedOrder.items.map(i => i.serviceName).join(', ')}</span></span>
                        </div>
                     </div>
                  </div>

                  <div className="flex items-center gap-2 w-full md:w-auto">
                     {user?.role === UserRole.DENTIST && (
                        (selectedOrder.status === OrderStatus.PENDING || selectedOrder.status === OrderStatus.NEEDS_REVIEW || selectedOrder.status === OrderStatus.RECEIVED) ? (
                           <Button size="sm" variant="secondary" onClick={() => { setIsModalOpen(true); setEditingOrder(selectedOrder); }} className="flex-1 md:flex-initial min-h-[44px]"><Edit2 size={16} /> <span className="md:inline">Edit Case</span></Button>
                        ) : (
                           <Button size="sm" variant="secondary" className="opacity-75 flex-1 md:flex-initial min-h-[44px]" onClick={() => {
                              if (confirm("This case is currently being processed and cannot be edited directly.\n\nWould you like to contact support via WhatsApp to request changes?")) {
                                 window.open(`https://wa.me/201556761102?text=I need to request changes for Order #${selectedOrder.id} (${selectedOrder.patientName})`, '_blank');
                              }
                           }}><div className="flex items-center gap-2"><Lock size={16} /> <span className="md:inline">Changes</span></div></Button>
                        )
                     )}
                     {(user?.role === UserRole.ADMIN || user?.role === UserRole.MODERATOR) && (
                        <>
                           <Button size="sm" variant="secondary" onClick={() => handleEditOrder(selectedOrder)} className="flex-1 md:flex-initial min-h-[44px]">
                              <Edit2 size={16} /> <span className="hidden md:inline">Edit</span>
                           </Button>
                           <div className="relative">
                              <button onClick={() => { setOpenMenuId(openMenuId === selectedOrder.id ? null : selectedOrder.id); }} className="p-2.5 hover:bg-surfaceHighlight rounded-lg transition-colors border border-transparent hover:border-border min-h-[44px] min-w-[44px] flex items-center justify-center"><MoreVertical size={20} className="text-secondary" /></button>
                              {openMenuId === selectedOrder.id && (
                                 <div className="absolute right-0 top-full mt-2 w-48 bg-surface border border-border rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in ring-1 ring-black/5">
                                    <button onClick={() => handleEditOrder(selectedOrder)} className="w-full text-left px-4 py-3 text-sm text-secondary hover:bg-surfaceHighlight hover:text-primary flex items-center gap-2"><Edit2 size={14} /> Edit Order</button>
                                    <button onClick={() => handleDeleteOrder(selectedOrder.id)} className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2"><Trash2 size={14} /> Delete</button>
                                    <button onClick={() => handleDuplicateOrder(selectedOrder)} className="w-full text-left px-4 py-3 text-sm text-secondary hover:bg-surfaceHighlight flex items-center gap-2"><CopyIcon size={14} /> Duplicate</button>
                                 </div>
                              )}
                           </div>
                        </>
                     )}
                     <button onClick={() => setSelectedOrder(null)} className="hidden md:flex p-2 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20 rounded-lg text-secondary transition-all border border-transparent items-center justify-center min-h-[44px] min-w-[44px]"><X size={24} /></button>
                  </div>
               </div>

               <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                  {/* Main Content Area */}
                  <div className="flex-1 flex flex-col overflow-y-auto bg-background">
                     {/* 3D Viewer / Preview Area */}
                     {/* 3D Viewer / Preview Area (Conditional) */}
                     {selectedOrder.files.some(f => f.name.toLowerCase().endsWith('.stl')) && (
                        <div className="h-[300px] md:h-[400px] bg-black relative border-b border-border group shrink-0">
                           <STLViewer url={selectedOrder.files.find(f => f.name.toLowerCase().endsWith('.stl'))?.url} />
                           <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button size="sm" variant="secondary" onClick={() => { }}><Maximize2 size={16} /></Button>
                           </div>
                           {/* Overlay Stats */}
                           <div className="absolute bottom-4 left-4 flex gap-4">
                              <div className="bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 text-xs font-mono text-zinc-300">
                                 {selectedOrder.files.length} Files
                              </div>
                              <div className="bg-black/60 backdrop-blur px-3 py-1.5 rounded-lg border border-white/10 text-xs font-mono text-zinc-300">
                                 Due: {new Date(selectedOrder.dueDate).toLocaleDateString()}
                              </div>
                           </div>
                        </div>
                     )}

                     {/* Tabs */}
                     <div className="flex border-b border-gray-800 sticky top-0 bg-[#111315] z-[100] overflow-x-auto scrollbar-hide min-h-[50px]">
                        <button onClick={() => setActiveDetailTab('overview')} className={`flex-1 min-w-[100px] md:min-w-0 py-3 md:py-4 text-xs md:text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeDetailTab === 'overview' ? 'border-primary text-white bg-white/10' : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'}`}>Overview</button>
                        <button onClick={() => setActiveDetailTab('communication')} className={`flex-1 min-w-[100px] md:min-w-0 py-3 md:py-4 text-xs md:text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeDetailTab === 'communication' ? 'border-primary text-white bg-white/10' : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'}`}>Chat</button>
                        <button onClick={() => setActiveDetailTab('files')} className={`flex-1 min-w-[100px] md:min-w-0 py-3 md:py-4 text-xs md:text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeDetailTab === 'files' ? 'border-primary text-white bg-white/10' : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'}`}>Files</button>
                     </div>

                     <div className="p-4 md:p-6">
                        {activeDetailTab === 'overview' && (
                           <div className="space-y-8 animate-fade-in">
                              {/* Action Banner based on Status */}
                              {selectedOrder.status === OrderStatus.NEEDS_REVIEW && user?.role === UserRole.DENTIST && (
                                 <div className="bg-blue-900/20 border border-blue-500/30 p-6 rounded-xl mb-6">
                                    <h3 className="text-blue-100 font-bold text-lg mb-2">Design Approval Required</h3>
                                    <p className="text-blue-300/80 text-sm mb-4">The lab has submitted a design for your review. Please inspect the 3D preview.</p>
                                    <div className="flex gap-4">
                                       <Button onClick={() => handleDoctorAction('approve')} className="bg-blue-600 hover:bg-blue-500 border-none">Approve & Pay</Button>
                                       <Button onClick={() => handleOpenFeedback('RE_DESIGN')} variant="danger">Request Changes</Button>
                                    </div>
                                 </div>
                              )}

                              {selectedOrder.status === OrderStatus.IN_TRY_IN && user?.role === UserRole.DENTIST && (
                                 <div className="bg-purple-900/20 border border-purple-500/30 p-6 rounded-xl mb-6">
                                    <h3 className="text-purple-100 font-bold text-lg mb-2">Try-In Feedback</h3>
                                    <p className="text-purple-300/80 text-sm mb-4">You have received the PMMA Try-In. Is it good to proceed?</p>
                                    <div className="flex gap-4">
                                       <Button onClick={handleTryInProceed} className="bg-purple-600 hover:bg-purple-500 border-none">Approved - Proceed to Final</Button>
                                       <Button onClick={() => handleOpenFeedback('RE_DESIGN')} variant="secondary">Re-Design</Button>
                                       <Button onClick={() => handleOpenFeedback('RE_TRY')} variant="danger">Re-Print (Fit Issue)</Button>
                                    </div>
                                 </div>
                              )}

                              <div className="mb-8">
                                 <h3 className="text-sm font-bold text-secondary uppercase mb-4">Odontogram</h3>
                                 <div className="w-full overflow-x-auto pb-4 flex flex-col items-center">
                                    <DentalArchSelector
                                       selectedTeeth={selectedOrder.toothNumber ? selectedOrder.toothNumber.split(',').map(t => t.trim()) : []}
                                       readOnly={true}
                                       toothColors={(() => {
                                          const colors: Record<string, { color: string, label: string }> = {};

                                          // Helper to get color from service name
                                          const getServiceColor = (name: string) => {
                                             const n = name.toLowerCase();
                                             if (n.includes('zirconia')) return '#3b82f6'; // Blue-500
                                             if (n.includes('emax') || n.includes('e.max')) return '#8b5cf6'; // Violet-500
                                             if (n.includes('pmma') || n.includes('try-in')) return '#f97316'; // Orange-500
                                             if (n.includes('metal') || n.includes('pfm')) return '#64748b'; // Slate-500
                                             if (n.includes('veneer')) return '#10b981'; // Emerald-500
                                             if (n.includes('implant') || n.includes('abutment')) return '#ec4899'; // Pink-500
                                             return '#3b82f6'; // Default Blue
                                          };

                                          // 1. Process Order Items (Preferred)
                                          if (selectedOrder.items && selectedOrder.items.length > 0) {
                                             selectedOrder.items.forEach(item => {
                                                item.teeth.forEach(tooth => {
                                                   colors[tooth] = {
                                                      color: getServiceColor(item.serviceName),
                                                      label: item.serviceName
                                                   };
                                                });
                                             });
                                          }
                                          // 2. Fallback to basic order details if no specific items
                                          else if (selectedOrder.toothNumber) {
                                             const defaultColor = selectedOrder.isPMMA ? '#f97316' : '#3b82f6';
                                             const defaultLabel = selectedOrder.isPMMA ? 'PMMA Try-In' : 'Zirconia';
                                             selectedOrder.toothNumber.split(',').map(t => t.trim()).forEach(tooth => {
                                                colors[tooth] = { color: defaultColor, label: defaultLabel };
                                             });
                                          }

                                          return colors;
                                       })()}
                                    />


                                 </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                 <div>
                                    <h3 className="text-sm font-bold text-secondary uppercase mb-4">Case Details</h3>
                                    <div className="space-y-4">
                                       <div className="flex justify-between py-2 border-b border-border">
                                          <span className="text-secondary">Doctor</span>
                                          <span className="text-primary">{selectedOrder.doctorName}</span>
                                       </div>
                                       <div className="flex justify-between py-2 border-b border-border">
                                          <span className="text-secondary">Shade</span>
                                          <span className="text-primary font-bold">{selectedOrder.shade}</span>
                                       </div>
                                       <div className="flex justify-between py-2 border-b border-border">
                                          <span className="text-secondary">Restoration</span>
                                          <span className="text-primary">{selectedOrder.isPMMA ? 'PMMA Try-In + Zirconia' : 'Zirconia / Emax'}</span>
                                       </div>
                                       <div className="mt-4 bg-surfaceHighlight p-4 rounded-xl border border-border">
                                          <span className="text-xs font-bold text-secondary uppercase block mb-2">Instructions</span>
                                          <p className="text-primary text-sm leading-relaxed">{selectedOrder.notes || 'No specific instructions.'}</p>
                                       </div>
                                    </div>
                                 </div>

                                 <div>
                                    <h3 className="text-sm font-bold text-secondary uppercase mb-4">Timeline</h3>
                                    <OrderTimeline orderId={selectedOrder.id} />
                                 </div>
                              </div>
                           </div>
                        )}

                        {activeDetailTab === 'communication' && (
                           <div className="animate-fade-in h-full">
                              <ChatWidget orderId={selectedOrder.id} user={user!} />
                           </div>
                        )}

                        {activeDetailTab === 'files' && (
                           <div className="space-y-6 animate-fade-in">
                              <div className="flex justify-between items-center">
                                 <h3 className="font-bold text-primary">Attached Files</h3>
                                 <label className="cursor-pointer bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors">
                                    <Upload size={16} /> Upload New
                                    <input type="file" multiple className="hidden" onChange={(e) => handleUploadToOrder(Array.from(e.target.files || []))} />
                                 </label>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 {selectedOrder.files.map(file => (
                                    <div key={file.id} className="bg-surface border border-border p-4 rounded-xl flex items-center justify-between group hover:border-secondary transition-colors">
                                       <div className="flex items-center gap-3 overflow-hidden">
                                          <div className="w-10 h-10 bg-surfaceHighlight rounded-lg flex items-center justify-center text-secondary border border-border">
                                             <FileText size={20} />
                                          </div>
                                          <div className="min-w-0">
                                             <div className="text-sm font-bold text-primary truncate">{file.name}</div>
                                             <div className="text-xs text-secondary">{new Date(file.uploadedAt).toLocaleDateString()} • {(file.size / 1024 / 1024).toFixed(2)} MB</div>
                                          </div>
                                       </div>
                                       <div className="flex gap-2">
                                          {file.name.toLowerCase().endsWith('.zip') && (
                                             <button
                                                onClick={() => handleInspectArchive(file)}
                                                className="p-2 text-blue-400 hover:bg-blue-900/20 bg-surfaceHighlight rounded-lg border border-transparent hover:border-blue-500/30 transition-all"
                                                title="Inspect Archive Content"
                                             >
                                                <FolderOpen size={18} />
                                             </button>
                                          )}
                                          <a href={file.url} download className="p-2 text-secondary hover:text-primary bg-surfaceHighlight hover:bg-border rounded-lg border border-transparent hover:border-secondary transition-all">
                                             <Download size={18} />
                                          </a>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        )}
                     </div>
                  </div>

                  {/* Sidebar (Admin/Tech Controls) */}
                  {(user?.role === UserRole.ADMIN || user?.role === UserRole.TECHNICIAN) && (
                     <div className="w-full md:w-80 bg-surface border-l border-border p-6 flex flex-col gap-6 overflow-y-auto">

                        {/* Status Control */}
                        <div className="space-y-3">
                           <h4 className="text-xs font-bold text-secondary uppercase">Status & Workflow</h4>
                           <Select
                              value={selectedOrder.status}
                              onChange={(e: any) => handleStatusChange(selectedOrder.id, e.target.value)}
                              options={Object.values(OrderStatus).map(s => ({ value: s, label: s.replace(/_/g, ' ') }))}
                              className="mb-0"
                           />

                           {selectedOrder.status === OrderStatus.DESIGNING && (
                              <Button onClick={() => setShowSubmitModal(true)} className="w-full">
                                 <Upload size={16} /> Submit Design
                              </Button>
                           )}

                           {selectedOrder.status === OrderStatus.MILLING && user.role === UserRole.ADMIN && (
                              <Button onClick={() => setShowMillingModal(true)} className="w-full bg-purple-600 hover:bg-purple-500 border-none">
                                 <Cpu size={16} /> Send to Milling
                              </Button>
                           )}
                        </div>

                        {/* Tech Assignment (Admin Only) */}
                        {user.role === UserRole.ADMIN && (
                           <div className="space-y-3 pt-6 border-t border-border">
                              <h4 className="text-xs font-bold text-secondary uppercase">Technician</h4>
                              <div className="flex items-center gap-3 mb-2">
                                 <div className="w-8 h-8 rounded-full bg-surfaceHighlight flex items-center justify-center text-secondary">
                                    <UserIcon size={14} />
                                 </div>
                                 <div className="flex-1">
                                    <div className="text-sm font-bold text-primary">
                                       {users.find(u => u.id === selectedOrder.technicianId)?.name || 'Unassigned'}
                                    </div>
                                 </div>
                              </div>
                              <div className="relative">
                                 <select
                                    className="w-full bg-surfaceHighlight border border-border rounded-lg py-2 px-3 text-sm text-primary outline-none focus:border-blue-500"
                                    onChange={(e) => handleAssignTechnician(e.target.value)}
                                    value={selectedOrder.technicianId || ''}
                                 >
                                    <option value="">Assign Technician...</option>
                                    {users.filter(u => u.role === UserRole.TECHNICIAN).map(u => (
                                       <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                 </select>
                              </div>
                           </div>
                        )}

                        {/* Share Link */}
                        <div className="pt-6 border-t border-border">
                           <h4 className="text-xs font-bold text-secondary uppercase mb-2">Public Share Link</h4>
                           <div className="bg-surfaceHighlight p-3 rounded-xl border border-border">
                              <div className="flex items-center gap-2 mb-2">
                                 <div className="p-1.5 bg-green-900/30 rounded text-green-500"><Share2 size={14} /></div>
                                 <span className="text-xs font-bold text-primary">Share with Patient</span>
                              </div>
                              <Button size="sm" variant="secondary" className="w-full text-xs" onClick={async () => {
                                 const token = await api.createShareLink(selectedOrder.id);
                                 navigator.clipboard.writeText(`${window.location.origin}/#/order/${token}`);
                                 alert("Link copied!");
                              }}>Copy Link</Button>
                           </div>
                        </div>

                     </div>
                  )}
               </div>
            </div>
         </div>
      );
   }

   const renderAdminContent = () => {
      return (
         <AdminPanel
            lang={lang}
            orders={orders}
            activeSection={activeTab} // Pass the activeTab from Layout/App state directly
            onOrderClick={setSelectedOrder}
         />
      );
   };

   // Routing logic for standalone views
   if (hash.startsWith('#/order/')) {
      const token = hash.split('/')[2];
      return <PublicOrderView token={token} />;
   }

   if (hash.startsWith('#/request-upload/')) {
      const token = hash.split('/')[2];
      return <PublicUploadView token={token} />;
   }

   if (hash.startsWith('#/contact-request/')) {
      const token = hash.split('/')[2];
      return <ContactRequestView token={token} />;
   }

   // Auto-collapse sidebar when opening Case Details (on large screens)
   useEffect(() => {
      if (selectedOrder && window.innerWidth >= 768) {
         setIsSidebarCollapsed(true);
      }
   }, [selectedOrder]);

   if (loading) return (
      <div className="min-h-screen bg-gray-50 dark:bg-zinc-900 flex flex-col items-center justify-center">
         <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
         <p className="text-gray-500 dark:text-gray-400 animate-pulse">Loading System...</p>
      </div>
   );

   if (!user) {
      if (hash.includes('login') || hash.includes('signup')) {
         return <Auth
            onLogin={(u) => { setUser(u); navigate('#/dashboard'); }}
            onBack={() => navigate('#/landing')}
            defaultMode={hash.includes('signup') ? 'signup' : 'login'}
            onSwitchMode={(mode) => navigate(`#/${mode}`)}
         />;
      } else if (hash.includes('order/')) {
         return <PublicOrderView />;
      } else if (hash.includes('request-upload/')) {
         return <PublicUploadView />;
      } else if (hash.includes('contact-request')) {
         return <ContactRequestView />;
      } else {
         return <LandingPage onGetStarted={() => navigate('#/signup')} onLogin={() => navigate('#/login')} lang={lang} setLang={setLang} />;
      }
   }
   if (user.role === UserRole.DENTIST && !user.onboardingComplete) {
      return <Onboarding user={user} onComplete={setUser} />;
   }

   return (
      <Layout
         user={user}
         onLogout={() => {
            api.logout().then(() => { setUser(null); navigate('#/landing'); });
         }}
         activeView={activeTab}
         onChangeView={handleTabChange}
         lang={lang}
         setLang={setLang}
         onTopUp={() => navigate('#/dashboard/invoices')}
         onNewCase={() => {
            setEditingOrder(null);
            setIsModalOpen(true);
         }}
         isCollapsed={isSidebarCollapsed}
         onCollapse={setIsSidebarCollapsed}
      >
         {/* Use simple conditional rendering for role-based content */}
         {(user.role === UserRole.ADMIN || user.role === UserRole.MODERATOR) && renderAdminContent()}

         {user.role === UserRole.DENTIST && (
            <>
               {activeTab === 'dashboard' && (
                  <DentistDashboard
                     user={user}
                     orders={orders}
                     onNavigate={navigate}
                     onNewCase={() => { setEditingOrder(null); setIsModalOpen(true); }}
                     onTopUp={() => navigate('#/dashboard/invoices')}
                  />
               )}
               {activeTab === 'orders' && (
                  <DentistOrders
                     user={user}
                     orders={orders}
                     onOrderClick={setSelectedOrder}
                     onNewCase={() => { setEditingOrder(null); setIsModalOpen(true); }}
                  />
               )}
               {activeTab === 'invoices' && (
                  <Financials
                     user={user}
                     orders={orders}
                     transactions={transactions}
                     myTopUpRequests={myTopUpRequests}
                     onTopUpRequest={handleTopUpRequest}
                  />
               )}
            </>
         )}

         {user.role === UserRole.TECHNICIAN && renderTechnicianContent()}

         {activeTab === 'settings' && <Settings user={user} onUpdateUser={setUser} />}
         {activeTab === 'price_list' && <PriceList user={user} />}
         {activeTab === 'clinic' && <ClinicManagement />}

         <OrderModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingOrder(null); }}
            onSubmit={handleSaveOrder}
            onQueueUpload={handleQueueUpload}
            user={user}
            initialOrder={editingOrder}
         />

         {renderOrderDetailModal()}

         {renderSubmitModal()}
         {renderMillingModal()}
         {renderFeedbackModal()}
         <UploadWidget uploads={uploads} onClose={handleRemoveUpload} />

         {showNotifications && (
            <>
               <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)}></div>
               <NotificationDropdown />
            </>
         )}
      </Layout>
   );
}